import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface TreatmentPlan {
  id: string;
  title: string;
  content: string;
  createdAt: Date;
  sessionId: string;
}

interface TreatmentPlanContextType {
  currentTreatmentPlan: TreatmentPlan | null;
  treatmentPlans: TreatmentPlan[];
  setCurrentTreatmentPlan: (plan: TreatmentPlan | null) => void;
  addTreatmentPlan: (plan: TreatmentPlan) => void;
  updateTreatmentPlan: (id: string, content: string) => void;
}

const TreatmentPlanContext = createContext<TreatmentPlanContextType | undefined>(undefined);

export const useTreatmentPlan = () => {
  const context = useContext(TreatmentPlanContext);
  if (!context) {
    throw new Error('useTreatmentPlan must be used within a TreatmentPlanProvider');
  }
  return context;
};

interface TreatmentPlanProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'orthomind_treatment_plans';

export const TreatmentPlanProvider: React.FC<TreatmentPlanProviderProps> = ({ children }) => {
  const [currentTreatmentPlan, setCurrentTreatmentPlan] = useState<TreatmentPlan | null>(null);
  const [treatmentPlans, setTreatmentPlans] = useState<TreatmentPlan[]>([]);

  // Load treatment plans from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedPlans = JSON.parse(stored);
        // Convert date strings back to Date objects
        const plansWithDates = parsedPlans.map((plan: any) => ({
          ...plan,
          createdAt: new Date(plan.createdAt),
        }));
        setTreatmentPlans(plansWithDates);
      }
    } catch (error) {
      console.error('Failed to load treatment plans from localStorage:', error);
    }
  }, []);

  // Save treatment plans to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(treatmentPlans));
    } catch (error) {
      console.error('Failed to save treatment plans to localStorage:', error);
    }
  }, [treatmentPlans]);

  const addTreatmentPlan = (plan: TreatmentPlan) => {
    setTreatmentPlans(prev => [...prev, plan]);
    setCurrentTreatmentPlan(plan);
  };

  const updateTreatmentPlan = (id: string, content: string) => {
    setTreatmentPlans(prev => 
      prev.map(plan => 
        plan.id === id ? { ...plan, content } : plan
      )
    );
    if (currentTreatmentPlan?.id === id) {
      setCurrentTreatmentPlan({ ...currentTreatmentPlan, content });
    }
  };

  return (
    <TreatmentPlanContext.Provider
      value={{
        currentTreatmentPlan,
        treatmentPlans,
        setCurrentTreatmentPlan,
        addTreatmentPlan,
        updateTreatmentPlan,
      }}
    >
      {children}
    </TreatmentPlanContext.Provider>
  );
};